"use client";

import { useMemo, useState, useCallback } from "react";
import {
    PieChart,
    Pie,
    ResponsiveContainer,
    Cell,
    Label,
    Sector,
} from "recharts";
import { CircleSlash, TrendingUp, TrendingDown } from "lucide-react";
import { cn } from "@/lib/utils";

// --- 1. Definisi Tipe Data ---

interface PaymentChartData {
    name: string;
    value: number;
    colorStart: string;
    colorEnd: string;
}

// Interface untuk Props komponen
type PaidUnpaidChartProps = {
    paid: number;
    unpaid: number;
};

// Interface untuk Props 'activeShape' (Data geometri yang dikirim Recharts saat hover)
interface PieActiveShapeProps {
    cx: number;
    cy: number;
    innerRadius: number;
    outerRadius: number;
    startAngle: number;
    endAngle: number;
    fill: string;
    payload?: PaymentChartData;
    value?: number;
}

// --- 2. Konfigurasi Warna ---
const CHART_CONFIG = {
    paid: { start: "#10b981", end: "#059669" }, // Emerald
    unpaid: { start: "#f43f5e", end: "#e11d48" }, // Rose
};

// --- 3. Custom Active Shape (Tanpa 'any') ---
// Kita cast props sebagai 'PieActiveShapeProps'
const renderActiveShape = (props: unknown) => {
    const {
        cx,
        cy,
        innerRadius,
        outerRadius,
        startAngle,
        endAngle,
        fill
    } = props as PieActiveShapeProps;

    return (
        <g>
            {/* Main Sector (Zoomed) */}
            <Sector
                cx={cx}
                cy={cy}
                innerRadius={innerRadius}
                outerRadius={outerRadius + 8} // Membesar 8px
                startAngle={startAngle}
                endAngle={endAngle}
                fill={fill}
                cornerRadius={6}
            />
            {/* Glow Effect Ring */}
            <Sector
                cx={cx}
                cy={cy}
                startAngle={startAngle}
                endAngle={endAngle}
                innerRadius={outerRadius + 12}
                outerRadius={outerRadius + 14}
                fill={fill}
                opacity={0.3}
                cornerRadius={10}
            />
        </g>
    );
};

export function PaidUnpaidChart({ paid, unpaid }: PaidUnpaidChartProps) {
    const [activeIndex, setActiveIndex] = useState<number>(-1);

    const total = paid + unpaid;
    const paidPercentage = total > 0 ? Math.round((paid / total) * 100) : 0;

    const data: PaymentChartData[] = useMemo(() => [
        {
            name: "Sudah Bayar",
            value: paid,
            colorStart: CHART_CONFIG.paid.start,
            colorEnd: CHART_CONFIG.paid.end
        },
        {
            name: "Belum Bayar",
            value: unpaid,
            colorStart: CHART_CONFIG.unpaid.start,
            colorEnd: CHART_CONFIG.unpaid.end
        },
    ], [paid, unpaid]);

    // Menggunakan 'unknown' untuk argumen pertama karena kita tidak memakainya,
    // tapi tetap menjaga type safety.
    const onPieEnter = useCallback((_: unknown, index: number) => {
        setActiveIndex(index);
    }, []);

    const onPieLeave = useCallback(() => {
        setActiveIndex(-1);
    }, []);

    // Empty State
    if (total === 0) {
        return (
            <div className="h-[280px] w-full flex flex-col items-center justify-center text-muted-foreground bg-slate-50/50 dark:bg-slate-900/50 rounded-xl border-2 border-dashed animate-in fade-in zoom-in-95 duration-500">
                <div className="p-4 rounded-full bg-slate-100 dark:bg-slate-800 mb-3">
                    <CircleSlash className="w-8 h-8 opacity-40" />
                </div>
                <p className="text-sm font-medium">Belum ada data transaksi</p>
            </div>
        );
    }

    return (
        <div className="w-full h-full flex flex-col items-center justify-between py-2">
            {/* --- Chart Area --- */}
            <div className="w-full h-[260px] relative">
                <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                        <defs>
                            {data.map((entry, index) => (
                                <linearGradient key={`grad-${index}`} id={`gradient-${index}`} x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor={entry.colorStart} stopOpacity={1} />
                                    <stop offset="100%" stopColor={entry.colorEnd} stopOpacity={1} />
                                </linearGradient>
                            ))}
                        </defs>

                        <Pie
                            activeIndex={activeIndex}
                            activeShape={renderActiveShape}
                            data={data}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            innerRadius={70}
                            outerRadius={95}
                            paddingAngle={4}
                            cornerRadius={6}
                            stroke="none"
                            onMouseEnter={onPieEnter}
                            onMouseLeave={onPieLeave}
                        >
                            {data.map((_, index) => (
                                <Cell
                                    key={`cell-${index}`}
                                    fill={`url(#gradient-${index})`}
                                    className="transition-all duration-300 ease-in-out outline-none focus:outline-none"
                                />
                            ))}

                            <Label
                                content={({ viewBox }) => {
                                    // Type checking manual untuk viewBox
                                    if (viewBox && "cx" in viewBox && "cy" in viewBox) {
                                        return (
                                            <text
                                                x={viewBox.cx}
                                                y={viewBox.cy}
                                                textAnchor="middle"
                                                dominantBaseline="middle"
                                            >
                                                <tspan
                                                    x={viewBox.cx}
                                                    y={viewBox.cy}
                                                    className="fill-foreground text-4xl font-bold tracking-tighter drop-shadow-sm"
                                                >
                                                    {paidPercentage}%
                                                </tspan>
                                                <tspan
                                                    x={viewBox.cx}
                                                    y={(viewBox.cy as number) + 24} // Cast number agar aman
                                                    className="fill-muted-foreground text-xs font-semibold uppercase tracking-widest"
                                                >
                                                    Terbayar
                                                </tspan>
                                            </text>
                                        );
                                    }
                                    return null;
                                }}
                            />
                        </Pie>
                    </PieChart>
                </ResponsiveContainer>
            </div>

            {/* --- Modern Legend Cards --- */}
            <div className="grid grid-cols-2 gap-3 w-full px-2 mt-2">
                {data.map((item, i) => {
                    const isPaid = item.name === "Sudah Bayar";
                    const percentage = Math.round((item.value / total) * 100);

                    return (
                        <div
                            key={i}
                            className={cn(
                                "flex flex-col p-3 rounded-xl border transition-all duration-300 cursor-default",
                                activeIndex === i ? "bg-accent/50 scale-[1.02] shadow-sm border-primary/20" : "bg-transparent border-transparent hover:bg-slate-50 dark:hover:bg-slate-800/50"
                            )}
                            onMouseEnter={() => setActiveIndex(i)}
                            onMouseLeave={() => setActiveIndex(-1)}
                        >
                            <div className="flex items-center justify-between mb-1">
                                <div className="flex items-center gap-2">
                                    <div
                                        className="w-2.5 h-2.5 rounded-full shadow-sm"
                                        style={{ background: `linear-gradient(to bottom right, ${item.colorStart}, ${item.colorEnd})` }}
                                    />
                                    <span className="text-xs text-muted-foreground font-medium">
                                        {item.name}
                                    </span>
                                </div>
                                {isPaid ? (
                                    <TrendingUp className="w-3 h-3 text-emerald-500" />
                                ) : (
                                    <TrendingDown className="w-3 h-3 text-rose-500" />
                                )}
                            </div>

                            <div className="flex items-baseline gap-2 pl-4">
                                <span className="text-xl font-bold tabular-nums text-foreground">
                                    {item.value}
                                </span>
                                <span className="text-xs font-medium text-muted-foreground">
                                    ({percentage}%)
                                </span>
                            </div>
                        </div>
                    )
                })}
            </div>
        </div>
    );
}